import React, { createContext, useState, useEffect, useContext } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../config/firebase';
import { adminLogin, verifyAdminToken } from '../api/admin';

const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing token on mount and listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // User is signed in, verify they have admin privileges
          const tokenResult = await user.getIdTokenResult();
          
          if (tokenResult.claims.admin) {
            // User is authenticated and has admin privileges
            localStorage.setItem('adminToken', tokenResult.token);
            setIsAdminAuthenticated(true);
            setAdminUser(user);
          } else {
            // User is authenticated but not an admin
            console.warn('User does not have admin privileges');
            localStorage.removeItem('adminToken');
            setIsAdminAuthenticated(false);
            setAdminUser(null);
          }
        } catch (error) {
          console.error('Error verifying admin status:', error);
          localStorage.removeItem('adminToken');
          setIsAdminAuthenticated(false);
          setAdminUser(null);
        }
      } else {
        // User is signed out
        localStorage.removeItem('adminToken');
        setIsAdminAuthenticated(false);
        setAdminUser(null);
      }
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  const login = async (credentials) => {
    try {
      setLoading(true);
      const response = await adminLogin(credentials);
      if(!response.claims.admin) {
        return { success: false, error: 'You do not have access to this information' };
      }
  
      if (response.token && response.claims.admin) {
        localStorage.setItem('adminToken', response.token);
        setIsAdminAuthenticated(true);
        setAdminUser(response.user);
        return { success: true };
      } else {
        return { success: false, error: response.message || 'Login failed' };
      }
    } catch (error) {
      console.error('Admin login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Login failed. Please try again.' 
      };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('adminToken');
      setIsAdminAuthenticated(false);
      setAdminUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear local state even if signOut fails
      localStorage.removeItem('adminToken');
      setIsAdminAuthenticated(false);
      setAdminUser(null);
    }
  };

  const value = {
    isAdminAuthenticated,
    adminUser,
    login,
    logout,
    loading
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export default AdminContext;
